'use client';
import { computeOfferPaymentBreakdown } from 'bff';
import { useTranslations } from 'i11n';
import { OFFER_SINGLE_PAYMENT_INSTALLMENTS } from 'piramid-constants';
import { useMemo } from 'react';
import { useNumberFormatter } from '../hooks/useNumberFormatter';

const DetailCell = ({ title, value }: { title: string; value: string }) => (
  <div className='flex min-w-0 flex-col gap-1'>
    <p className='text-sm font-semibold uppercase tracking-wide text-dark'>
      {title}
    </p>
    <p className='text-sm text-stone-900'>{value}</p>
  </div>
);

/**
 * Las condiciones de pago de un ofrecimiento guiado, una por celda: qué cobra
 * el reclamante en cada cuota, a cuántos días y qué se lleva el letrado. Los
 * importes salen de `computeOfferPaymentBreakdown`, el mismo cálculo que el
 * wizard muestra en vivo, así que el detalle no puede decir otra cosa. Las
 * celdas van sueltas: las acomoda la grilla de quien las usa.
 */
export const OfferPaymentBreakdownDetail = ({
  amount,
  installments,
  fee_percentage,
  payment_days,
  currency,
}: {
  amount: number;
  installments: number;
  fee_percentage: number | null;
  payment_days: number;
  currency?: string;
}) => {
  const t = useTranslations('claim.negotiation.offer_card');
  const { formatNumber } = useNumberFormatter(currency);

  const breakdown = useMemo(
    () =>
      computeOfferPaymentBreakdown({
        amount,
        installments,
        feePercentage: fee_percentage,
      }),
    [amount, fee_percentage, installments],
  );

  if (!breakdown) return null;

  const isSinglePayment =
    breakdown.installments === OFFER_SINGLE_PAYMENT_INSTALLMENTS ||
    breakdown.installmentAmount === null;

  return (
    <>
      <DetailCell title={t('offered_amount')} value={formatNumber(amount)} />
      <DetailCell
        title={t('installments_title')}
        value={
          isSinglePayment
            ? t('single_payment')
            : t('installments_value', {
                count: breakdown.installments,
                amount: formatNumber(breakdown.installmentAmount),
              })
        }
      />
      <DetailCell
        title={t('payment_days_title')}
        value={t('days_value', { count: payment_days })}
      />
      <DetailCell
        title={t('fees_title')}
        value={
          breakdown.fees === null
            ? t('no_fees')
            : t('fees_value', {
                percentage: fee_percentage,
                amount: formatNumber(breakdown.fees),
              })
        }
      />
    </>
  );
};
